import { defineExperiment } from '@learnforge/demo-render/harness';
import type {
  ExperimentSpec,
  PanelRow,
  ActionBarSpec,
  HarnessController,
} from '@learnforge/demo-render/harness';
import type {
  CartesianViewportHandle,
  CartesianViewportView,
  FigureElement,
  FigureHandle,
  FormulaLegendHandle,
  ReadoutHandle,
  ReadoutRow,
} from '@learnforge/demo-render';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { createCartesianViewport, createFormulaLegend, createSvgEl } from '@learnforge/demo-render';
import type { CalculusContext } from '../types';
import type { CalcKey } from '../locales/keys';
import { createCurveLayer, type CurveLayerHandle } from '../render/curve';
import { createDualPane, type DualPaneHandle } from '../render/dual-pane';
import { isPointInCartesianDomain } from '../render/cartesian-plane';
import { compileFn, sampleFn, criticalPoints } from '../calc';
import { DERIVATIVE_PRESETS } from '../data/derivative-presets';
import { parse, derivative, simplify, stringify } from '@learnforge/demo-math/expr';
import { clamp, formatNumber } from '@learnforge/demo-math/numeric';

// ─── Stage layout constants ───────────────────────────────────────────────────

const STAGE_W = 800;
const STAGE_H = 460;
const PANE_GAP = 20;
/** Number of sample points per curve. */
const SAMPLE_N = 400;
/** Tangent segment half-length in world units. */
const TANGENT_HALF = 1.5;

// ─── State ────────────────────────────────────────────────────────────────────

export interface DerivativePlotterState {
  presetId: string;
  cursorX: number;
  /** Gates the f″ curve + concavity shading; defaults to ctx.advanced at init. */
  showSecond: boolean;
}

// ─── Exported pure helpers (unit-tested) ─────────────────────────────────────

/**
 * Compute the simplified symbolic first and second derivatives of a source
 * expression (in variable `x`).
 *
 * @returns `{ fPrime, fDoublePrime }` — stringified simplified derivative sources.
 */
export function derivativeSources(source: string): { fPrime: string; fDoublePrime: string } {
  const node = parse(source).node;
  const primeNode = simplify(derivative(node, 'x'));
  const doublePrimeNode = simplify(derivative(primeNode, 'x'));
  return {
    fPrime: stringify(primeNode),
    fDoublePrime: stringify(doublePrimeNode),
  };
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

/**
 * Sample `f` over `domain` and return [min, max] of the finite y-values,
 * padded by `pad` (absolute world units) on each side.
 */
function sampleYRange(
  f: (x: number) => number,
  domain: [number, number],
  n: number,
  pad: number,
): [number, number] {
  const pts = sampleFn(f, domain[0], domain[1], n);
  let yMin = Infinity;
  let yMax = -Infinity;
  for (const p of pts) {
    if (Number.isFinite(p.y)) {
      if (p.y < yMin) yMin = p.y;
      if (p.y > yMax) yMax = p.y;
    }
  }
  if (!Number.isFinite(yMin) || !Number.isFinite(yMax)) {
    return [-5, 5];
  }
  // Ensure minimum height so the pane is never zero-height
  const span = yMax - yMin;
  const extra = Math.max(pad, span * 0.15);
  return [yMin - extra, yMax + extra];
}

// ─── Spec factory ─────────────────────────────────────────────────────────────

function buildSpec(): ExperimentSpec<DerivativePlotterState, CalcKey, CalculusContext> {
  // Closure-stashed handles created in setup():
  let dual: DualPaneHandle;
  let topCurves: CurveLayerHandle;
  let bottomCurves: CurveLayerHandle;
  let figure: FigureHandle;
  let readout: ReadoutHandle;
  let viewport: CartesianViewportHandle;
  let view: CartesianViewportView;
  let legend: FormulaLegendHandle;
  let topBaseY: [number, number];
  let bottomBaseY: [number, number];
  let activeBaseKey = '';
  // Advanced-mode concavity shading group — recreated each render, cleaned up on dispose.
  let shadingGroup: SVGGElement | null = null;

  function defaultState(advanced: boolean): DerivativePlotterState {
    const preset = DERIVATIVE_PRESETS[0]!;
    return {
      presetId: preset.id,
      cursorX: (preset.domain[0] + preset.domain[1]) / 3,
      showSecond: advanced,
    };
  }

  function applyPresetLocal(state: DerivativePlotterState, id: string): DerivativePlotterState {
    const preset = DERIVATIVE_PRESETS.find((p) => p.id === id);
    if (!preset) return state;
    const mid = (preset.domain[0] + preset.domain[1]) / 3;
    return { ...state, presetId: preset.id, cursorX: mid };
  }

  function getPreset(state: DerivativePlotterState) {
    return DERIVATIVE_PRESETS.find((p) => p.id === state.presetId) ?? DERIVATIVE_PRESETS[0]!;
  }

  function clearShadingGroup(): void {
    if (shadingGroup) {
      shadingGroup.remove();
      shadingGroup = null;
    }
  }

  function fittedDomains(preset: (typeof DERIVATIVE_PRESETS)[number], showSecond: boolean) {
    const f = compileFn(preset.source);
    const { fPrime, fDoublePrime } = derivativeSources(preset.source);
    const topYDomain = sampleYRange(f, preset.domain, SAMPLE_N, 1);
    const primeY = sampleYRange(compileFn(fPrime), preset.domain, SAMPLE_N, 1);
    const secondY = showSecond
      ? sampleYRange(compileFn(fDoublePrime), preset.domain, SAMPLE_N, 1)
      : null;
    const bottomYDomain: [number, number] = secondY
      ? [Math.min(primeY[0], secondY[0]), Math.max(primeY[1], secondY[1])]
      : primeY;
    return { xDomain: preset.domain, topYDomain, bottomYDomain };
  }

  function mappedYDomain(base: [number, number], normalized: [number, number]): [number, number] {
    const span = base[1] - base[0];
    return [base[0] + normalized[0] * span, base[0] + normalized[1] * span];
  }

  function applyView(next: CartesianViewportView): void {
    view = next;
    dual.setDomains({
      xDomain: next.xDomain,
      topYDomain: mappedYDomain(topBaseY, next.yDomain),
      bottomYDomain: mappedYDomain(bottomBaseY, next.yDomain),
    });
  }

  return {
    labelKey: 'experiment.derivative-plotter',
    demoId: 'calculus',
    advancedKey: 'derivative-plotter',

    initialState(ctx): DerivativePlotterState {
      return defaultState(ctx.advanced);
    },

    applyPreset(state, id): DerivativePlotterState {
      return applyPresetLocal(state, id);
    },

    setup(ctx, controller: HarnessController<DerivativePlotterState>): void {
      // Pick y-domains from the first preset to size the panes.
      const preset = DERIVATIVE_PRESETS[0]!;
      const xDomain = preset.domain;
      const fitted = fittedDomains(preset, ctx.advanced);
      const topYDomain = fitted.topYDomain;
      const bottomYDomain = fitted.bottomYDomain;
      topBaseY = topYDomain;
      bottomBaseY = bottomYDomain;
      activeBaseKey = `${preset.id}:${ctx.advanced}`;

      dual = createDualPane(controller.svg, {
        xDomain,
        topYDomain,
        bottomYDomain,
        width: STAGE_W,
        height: STAGE_H,
        gap: PANE_GAP,
      });

      // Two curve layers — one per pane.
      topCurves = createCurveLayer(dual.topContent, dual.top);
      controller.registerCleanup(() => topCurves.destroy());

      bottomCurves = createCurveLayer(dual.bottomContent, dual.bottom);
      controller.registerCleanup(() => bottomCurves.destroy());

      // Draw axes once (they're static).
      dual.drawAxes();

      figure = controller.createFigure();
      readout = controller.createReadout();
      legend = createFormulaLegend(ctx.root);
      controller.registerCleanup(() => legend.destroy());

      viewport = createCartesianViewport(controller.svg, {
        base: { xDomain, yDomain: [0, 1] },
        plotRect: { x: 0, y: 0, width: STAGE_W, height: STAGE_H },
        onChange(next) {
          applyView(next);
          controller.requestRender();
        },
      });
      view = viewport.getView();
      controller.registerCleanup(() => viewport.dispose());

      // Register cleanup for the dual pane itself and shading group.
      controller.registerCleanup(() => {
        clearShadingGroup();
        dual.destroy();
      });
    },

    render(state: DerivativePlotterState, ctx): void {
      const preset = getPreset(state);
      const { cursorX } = state;
      const showSecond = ctx.advanced && state.showSecond;
      const nextBaseKey = `${preset.id}:${showSecond}`;
      if (nextBaseKey !== activeBaseKey) {
        const fitted = fittedDomains(preset, showSecond);
        activeBaseKey = nextBaseKey;
        topBaseY = fitted.topYDomain;
        bottomBaseY = fitted.bottomYDomain;
        viewport.setBase({ xDomain: fitted.xDomain, yDomain: [0, 1] }, { notify: false });
        view = viewport.getView();
        applyView(view);
      }
      const [xMin, xMax] = view.xDomain;

      // ── Compile functions ──
      const f = compileFn(preset.source);
      const { fPrime: fPrimeSrcEarly, fDoublePrime: fDoublePrimeSrcEarly } = derivativeSources(
        preset.source,
      );
      const fPrimeFnEarly = compileFn(fPrimeSrcEarly);
      const fDoublePrimeFnEarly = showSecond ? compileFn(fDoublePrimeSrcEarly) : null;

      // Reuse the already-computed derivative sources and compiled fns.
      const fPrimeSrc = fPrimeSrcEarly;
      const fDoublePrimeSrc = fDoublePrimeSrcEarly;
      const fPrimeFn = fPrimeFnEarly;
      const fDoublePrimeFn = fDoublePrimeFnEarly;

      // ── Current cursor values ──
      const fAtCursor = (() => {
        try {
          return f(cursorX);
        } catch {
          return NaN;
        }
      })();
      const fPrimeAtCursor = (() => {
        try {
          return fPrimeFn(cursorX);
        } catch {
          return NaN;
        }
      })();

      // ── Sample f for the top pane ──
      const fPts = sampleFn(f, xMin, xMax, SAMPLE_N);
      topCurves.setPolyline('f', fPts, 'calc-curve-primary');

      // ── Tangent segment in the top pane ──
      // Slope = f′(cursorX); draw a short segment of ± TANGENT_HALF world units.
      if (Number.isFinite(fAtCursor) && Number.isFinite(fPrimeAtCursor)) {
        const m = fPrimeAtCursor;
        const x0 = cursorX - TANGENT_HALF;
        const x1 = cursorX + TANGENT_HALF;
        const y0 = fAtCursor + m * (x0 - cursorX);
        const y1 = fAtCursor + m * (x1 - cursorX);
        topCurves.setPolyline(
          'tangent',
          [
            { x: x0, y: y0 },
            { x: x1, y: y1 },
          ],
          'calc-curve-tangent',
        );
      } else {
        topCurves.clear('tangent');
      }

      // ── Sample f′ for the bottom pane ──
      const fPrimePts = sampleFn(fPrimeFn, xMin, xMax, SAMPLE_N);
      bottomCurves.setPolyline('fPrime', fPrimePts, 'calc-curve-secondary');

      // ── Advanced: f″ curve ──
      if (showSecond && fDoublePrimeFn) {
        const fDoublePts = sampleFn(fDoublePrimeFn, xMin, xMax, SAMPLE_N);
        bottomCurves.setPolyline('fDoublePrime', fDoublePts, 'calc-curve-second');
      } else {
        bottomCurves.clear('fDoublePrime');
      }

      // ── Advanced: concavity shading (polygon in the top pane) ──
      clearShadingGroup();

      if (showSecond && fDoublePrimeFn) {
        // Collect sample points; split into concave-up (f″>0) and concave-down (f″<0) runs.
        // For each run, build a polygon: top edge = f(x) values, bottom edge = x-axis (y=0).
        const steps = SAMPLE_N;
        const dx = (xMax - xMin) / steps;

        // Group consecutive samples by sign of f″.
        type RunKind = 'up' | 'down';
        interface Run {
          kind: RunKind;
          pts: Vec2[];
        }
        const runs: Run[] = [];
        let currentRun: Run | null = null;

        for (let i = 0; i <= steps; i++) {
          const x = xMin + i * dx;
          const fpp = (() => {
            try {
              return fDoublePrimeFn(x);
            } catch {
              return NaN;
            }
          })();
          const fy = (() => {
            try {
              return f(x);
            } catch {
              return NaN;
            }
          })();
          if (!Number.isFinite(fpp) || !Number.isFinite(fy)) {
            currentRun = null;
            continue;
          }
          const kind: RunKind = fpp >= 0 ? 'up' : 'down';
          if (!currentRun || currentRun.kind !== kind) {
            currentRun = { kind, pts: [] };
            runs.push(currentRun);
          }
          currentRun.pts.push({ x, y: fy });
        }

        if (runs.length > 0) {
          shadingGroup = createSvgEl('g');
          shadingGroup.setAttribute('class', 'calc-concavity-shading');

          // y = 0 in world coords projected to screen via top pane projector.
          const yAxisScreenY = dual.top.worldToScreen({ x: 0, y: 0 }).y;

          for (const run of runs) {
            if (run.pts.length < 2) continue;
            // Build polygon: curve pts forward, then baseline pts backward.
            const screenPts = run.pts.map((p) => dual.top.worldToScreen(p));
            // Baseline: same x coords, y = xAxis screen y.
            const baselineFirst = { x: screenPts[0]!.x, y: yAxisScreenY };
            const baselineLast = { x: screenPts[screenPts.length - 1]!.x, y: yAxisScreenY };

            // Polygon: forward along curve, then back along baseline.
            const polyPts: Vec2[] = [baselineFirst, ...screenPts, baselineLast];

            const pointsStr = polyPts.map((p) => `${p.x},${p.y}`).join(' ');
            const poly = createSvgEl('polygon');
            poly.setAttribute(
              'class',
              run.kind === 'up' ? 'calc-concavity-up' : 'calc-concavity-down',
            );
            poly.setAttribute('points', pointsStr);
            shadingGroup.appendChild(poly);
          }

          // Insert before the top curve layer group so shading is behind curves.
          dual.topContent.insertBefore(shadingGroup, topCurves.group);
        }
      }

      // ── Figure elements ──
      // pt-cursor: draggable, projected onto the top pane's x-axis (y=0 in world).
      const cursorPoint = { x: cursorX, y: 0 };
      const cursorScreenPos = dual.top.worldToScreen(cursorPoint);
      // tie-dot: static, on f′ at cursorX in the bottom pane.
      const tieDotPos = Number.isFinite(fPrimeAtCursor)
        ? dual.bottom.worldToScreen({ x: cursorX, y: fPrimeAtCursor })
        : null;
      // vertical guide: full-stage-height vertical line at the cursor's screen x.
      const guideTopY = 0; // top of SVG
      const guideBotY = STAGE_H; // bottom of SVG

      const elements: FigureElement[] = [];
      if (isPointInCartesianDomain(cursorPoint, dual.top.getDomains())) {
        elements.push({
          kind: 'point',
          id: 'pt-cursor',
          at: cursorScreenPos,
          style: { className: 'geo-point-draggable' },
          draggable: {},
        });
      }
      elements.push(
        // Vertical dashed guide through cursorX across both panes.
        {
          kind: 'point',
          id: 'guide-top',
          at: { x: cursorScreenPos.x, y: guideTopY },
          style: { className: 'geo-point-off-stage' },
        },
        {
          kind: 'point',
          id: 'guide-bot',
          at: { x: cursorScreenPos.x, y: guideBotY },
          style: { className: 'geo-point-off-stage' },
        },
        {
          kind: 'segment',
          id: 'guide-line',
          from: 'guide-top',
          to: 'guide-bot',
          style: { className: 'geo-line geo-line--faint', dashed: true },
        },
      );

      // Tie-dot on f′ in the bottom pane.
      if (
        tieDotPos &&
        isPointInCartesianDomain({ x: cursorX, y: fPrimeAtCursor }, dual.bottom.getDomains())
      ) {
        elements.push({
          kind: 'point',
          id: 'tie-dot',
          at: tieDotPos,
          style: { className: 'geo-point-main' },
        });
      }

      // Advanced: dot on f at cursor.
      if (
        Number.isFinite(fAtCursor) &&
        isPointInCartesianDomain({ x: cursorX, y: fAtCursor }, dual.top.getDomains())
      ) {
        const fDotPos = dual.top.worldToScreen({ x: cursorX, y: fAtCursor });
        elements.push({
          kind: 'point',
          id: 'f-dot',
          at: fDotPos,
          style: { className: 'geo-point-secondary' },
        });
      }

      figure.update(elements);
      topCurves.group.ownerSVGElement
        ?.querySelector('[data-figure-id="pt-cursor"]')
        ?.setAttribute('data-no-pan', 'true');

      legend.setEntries([
        { id: 'f', formula: `f(x) = ${preset.source}`, color: 'var(--accent)' },
        { id: 'f-prime', formula: `f′(x) = ${fPrimeSrc}`, color: 'var(--ink-soft)' },
        ...(showSecond
          ? [
              {
                id: 'f-double-prime',
                formula: `f″(x) = ${fDoublePrimeSrc}`,
                color: 'var(--red)',
                lineStyle: 'dashed' as const,
              },
            ]
          : []),
      ]);

      // ── Readout ──
      const rows = buildReadout(state, fPrimeSrc, fDoublePrimeSrc, fPrimeFn, ctx.advanced);
      readout.update(rows);
    },

    onDrag(state: DerivativePlotterState, pointId: string, at: Vec2): DerivativePlotterState {
      if (pointId === 'pt-cursor') {
        const preset = getPreset(state);
        const worldX = dual.top.screenToWorld(at).x;
        const clamped = clamp(worldX, preset.domain[0], preset.domain[1]);
        return { ...state, cursorX: clamped };
      }
      return state;
    },

    panel(state: DerivativePlotterState, _ctx): PanelRow<CalcKey, DerivativePlotterState>[] {
      const rows: PanelRow<CalcKey, DerivativePlotterState>[] = [];

      // Preset dropdown.
      rows.push({
        kind: 'dropdown',
        id: 'derivative-preset',
        labelKey: 'derivative.panel.preset',
        options: DERIVATIVE_PRESETS.map((p) => ({ value: p.id, labelKey: p.labelKey })),
        value: state.presetId,
        onChange: (v) => applyPresetLocal(state, v),
      });

      // Cursor x slider.
      const preset = getPreset(state);
      rows.push({
        kind: 'slider',
        id: 'derivative-cursor-x',
        labelKey: 'derivative.panel.cursor-x',
        min: preset.domain[0],
        max: preset.domain[1],
        step: 0.05,
        value: state.cursorX,
        format: (v) => v.toFixed(2),
        onChange: (v) => ({ ...state, cursorX: v }),
      });

      // Advanced toggle.
      rows.push({
        kind: 'advanced-toggle',
        labelKey: 'panel.advanced',
      });

      return rows;
    },

    actions: (
      _state: DerivativePlotterState,
      ctx,
    ): ActionBarSpec<CalcKey, DerivativePlotterState> => {
      return {
        onReset: () => defaultState(ctx.advanced),
        onRandomise: (s) => {
          const idx = Math.floor(ctx.prng() * DERIVATIVE_PRESETS.length);
          const preset = DERIVATIVE_PRESETS[idx]!;
          const mid = (preset.domain[0] + preset.domain[1]) / 3;
          return { ...s, presetId: preset.id, cursorX: mid };
        },
        presets: DERIVATIVE_PRESETS.map((p) => ({ id: p.id, labelKey: p.labelKey })),
        onPreset: (s, id) => applyPresetLocal(s, id),
        viewReset: { labelKey: 'action.reset-view', onResetView: () => viewport.reset() },
      };
    },

    applyShotState(state: DerivativePlotterState, _shotId: string): DerivativePlotterState {
      // Pin cursorX to a slope-rich location (roughly 1/3 into the domain).
      const preset = getPreset(state);
      const [xMin, xMax] = preset.domain;
      const pinX = xMin + (xMax - xMin) * 0.35;
      return { ...state, cursorX: pinX, showSecond: true };
    },
  };
}

// ─── Readout builder ──────────────────────────────────────────────────────────

function buildReadout(
  state: DerivativePlotterState,
  fPrimeSrc: string,
  fDoublePrimeSrc: string,
  fPrimeFn: (x: number) => number,
  advanced: boolean,
): ReadoutRow[] {
  const { cursorX } = state;

  const fPrimeAtCursor = (() => {
    try {
      return fPrimeFn(cursorX);
    } catch {
      return NaN;
    }
  })();

  // Critical/inflection points.
  const critPts = (() => {
    try {
      const preset =
        DERIVATIVE_PRESETS.find((p) => p.id === state.presetId) ?? DERIVATIVE_PRESETS[0]!;
      const [x0, x1] = preset.domain;
      const fPrimeFnLocal = compileFn(fPrimeSrc);
      return criticalPoints(fPrimeFnLocal, x0, x1);
    } catch {
      return [];
    }
  })();

  const critStr = critPts.length === 0 ? 'none' : critPts.map((x) => x.toFixed(2)).join(', ');

  const rows: ReadoutRow[] = [
    {
      id: 'f-prime-symbolic',
      lhs: 'f′(x)',
      relation: '=' as const,
      rhs: fPrimeSrc,
      emphasis: 'invariant' as const,
    },
    {
      id: 'f-prime-at-cursor',
      lhs: 'f′(cursor)',
      relation: '=' as const,
      rhs: formatNumber(fPrimeAtCursor, { decimals: 4 }),
      emphasis: 'neutral' as const,
    },
    {
      id: 'critical-points',
      lhs: 'critical x',
      relation: '=' as const,
      rhs: critStr,
      emphasis: 'neutral' as const,
    },
  ];

  if (advanced) {
    rows.push({
      id: 'f-double-prime-symbolic',
      lhs: 'f″(x)',
      relation: '=' as const,
      rhs: fDoublePrimeSrc,
      emphasis: 'neutral' as const,
    });

    // Inflection points (where f″ changes sign = critical points of f′).
    const inflPts = (() => {
      try {
        const preset =
          DERIVATIVE_PRESETS.find((p) => p.id === state.presetId) ?? DERIVATIVE_PRESETS[0]!;
        const [x0, x1] = preset.domain;
        const fDoublePrimeFn = compileFn(fDoublePrimeSrc);
        return criticalPoints(fDoublePrimeFn, x0, x1);
      } catch {
        return [];
      }
    })();

    rows.push({
      id: 'inflection-points',
      lhs: 'inflection x',
      relation: '=' as const,
      rhs: inflPts.length === 0 ? 'none' : inflPts.map((x) => x.toFixed(2)).join(', '),
      emphasis: 'neutral' as const,
    });
  }

  return rows;
}

export const experiment = defineExperiment(buildSpec());
